import {
  AssetType,
  FireTVCampaignAutomationClient,
  PresignedUrlAction,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatFileUpload } from '@amzn/katal-react';
import mime from 'mime-types';
import { AssetConstants } from 'src/constants';
import { logger } from 'src/logger';
import { AssetState, AssetStatus, IAssetInputHandler } from 'src/types';
import { ControlPlaneClientUtils } from 'src/utils/clients/controlPlane';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { AssetMetadataSpecs, DimensionSpecs } from 'src/utils/Types';
import { validateAssetMetadata } from 'src/utils/ValidationUtils';
import { v4 as uuid } from 'uuid';

interface BasicValidationProps {
  allowedContentTypes: string[];
  expectedAspectRatio?: number;
  maxFileSizeInBytes: number;
  minResolution: { height: number; width: number };
  maxResolution: { height: number; width: number };
}

interface AbstractAssetInputHandlerProps extends BasicValidationProps {
  assetKeyPrefix: string;
  assetType: AssetType;
  controlPlaneClient: FireTVCampaignAutomationClient;
  setState: (assetState: Partial<AssetState>) => void;
}

const TIMEOUT_IN_MS = 5000;
const FALLBACK_FILE_NAME = 'unnamed';

/**
 * Abstract base class for handling asset input operations with validation and upload capabilities.
 * Implements the IAssetInputHandler interface to provide consistent asset handling behavior.
 */
abstract class AbstractAssetInputHandler implements IAssetInputHandler {
  protected readonly controlPlaneClient: FireTVCampaignAutomationClient;
  protected readonly validationProps: BasicValidationProps;
  private readonly assetKeyPrefix: string;
  private readonly setState: (assetState: Partial<AssetState>) => void;
  protected readonly assetType: AssetType;

  constructor({
    assetKeyPrefix,
    assetType,
    setState,
    controlPlaneClient,
    ...validationProps
  }: AbstractAssetInputHandlerProps) {
    this.assetKeyPrefix = assetKeyPrefix;
    this.assetType = assetType;
    this.setState = setState;
    this.validationProps = validationProps;
    this.controlPlaneClient = controlPlaneClient;

    this.handleUrl = this.handleUrl.bind(this);
    this.handleFileAttached = this.handleFileAttached.bind(this);
    this.handleFileRemoved = this.handleFileRemoved.bind(this);
    this.handleFileReplaced = this.handleFileReplaced.bind(this);
    this.handleFileAttachedError = this.handleFileAttachedError.bind(this);
    this.setInProgress = this.setInProgress.bind(this);
    this.clearError = this.clearError.bind(this);
  }

  /**
   * Clears any error state in the asset handler
   */
  clearError(): void {
    logger.debug('Clearing error');
    this.setState({ error: undefined });
  }

  /**
   * Handles file attachment events from KatFileUpload component
   * @param {KatFileUpload.FilesAttachedEvent} fileAttachedEvent - Event containing attached file information
   * @returns {Promise<void>}
   */
  async handleFileAttached(fileAttachedEvent: KatFileUpload.FilesAttachedEvent): Promise<void> {
    logger.debug('File attached', { fileAttachedEvent });
    this.setInProgress();
    try {
      const file = fileAttachedEvent.detail.files[0];
      await this.handleFileObject(file);
    } catch (error: unknown) {
      const fileName = fileAttachedEvent.detail.files[0]?.name;
      const errorMsg =
        error instanceof Error &&
        error.message &&
        (error.message.includes('Failed to upload file') ||
          error.message.includes('Failed to get presigned URL'))
          ? getErrorMessage(error)
          : fileName
            ? `Failed to upload file '${fileName}': ${getErrorMessage(error)}`
            : getErrorMessage(error);

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMsg,
      });
    }
  }

  /**
   * Handles file attachment error events
   * @param {KatFileUpload.FilesAttachedErrorEvent} fileAttachedErrorEvent - Error event from file attachment
   */
  handleFileAttachedError(fileAttachedErrorEvent: KatFileUpload.FilesAttachedErrorEvent): void {
    logger.error('File attached error', { fileAttachedErrorEvent });
    // Setting the error to undefined since the KatFileUpload will auto display the error
    this.setState({ status: AssetStatus.FAILED, error: undefined });
  }

  /**
   * Handles file removal events
   */
  handleFileRemoved(): void {
    logger.debug('File removed');
    this.setState({ status: AssetStatus.PENDING_INPUT, error: undefined });
  }

  /**
   * Handles file replacement events
   * @param {KatFileUpload.FilesReplacedEvent} fileReplacedEvent - Event containing new file information
   * @returns {Promise<void>}
   */
  async handleFileReplaced(fileReplacedEvent: KatFileUpload.FilesReplacedEvent): Promise<void> {
    logger.debug('File replaced', { fileReplacedEvent });
    this.setInProgress();
    try {
      const file = fileReplacedEvent.detail.newFiles[0];
      await this.handleFileObject(file);
    } catch (error: unknown) {
      const fileName = fileReplacedEvent.detail.newFiles[0]?.name;
      const errorMsg = this.fetchErrorMessage(error, fileName);

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMsg,
      });
    }
  }

  /**
   * Processes a URL input for asset handling
   * @param {string} urlInput - URL to process
   * @returns {Promise<void>}
   */
  async handleUrl(urlInput: string): Promise<void> {
    logger.debug('Handling URL', { urlInput });
    this.setInProgress();
    try {
      const urlMetadata = await this.fetchUrlMetadata(urlInput);
      const specs: AssetMetadataSpecs = {
        maxSize: this.validationProps.maxFileSizeInBytes,
        allowedTypes: this.validationProps.allowedContentTypes,
      };
      validateAssetMetadata(urlMetadata, specs);
      const fileObject = await this.fetchUrlFileObject(urlInput);
      await this.handleFileObject(fileObject);
    } catch (error: unknown) {
      this.setState({
        status: AssetStatus.FAILED,
        error: getErrorMessage(error),
      });
    }
  }

  /**
   * Abstract method to validate file objects. Must be implemented by concrete classes.
   * @param {File} fileObject - File to validate
   * @returns {Promise<void>}
   */
  protected abstract validateFileObject(fileObject: File, specs: DimensionSpecs): Promise<void>;

  /**
   * Fetches a presigned URL for file upload
   * @param {File} fileObject - File to get presigned URL for
   * @returns {Promise<{key: string, presignedUrl: string}>}
   * @throws {Error} When control plane client is not provided or presigned URL fetch fails
   * @private
   */
  private async fetchPresignedUrl(
    fileObject: File,
  ): Promise<{ key: string; presignedUrl: string }> {
    logger.debug('Fetching presigned URL from control plane', {
      fileObject,
      assetKeyPrefix: this.assetKeyPrefix,
    });

    const key = this.generateS3Key(this.assetKeyPrefix, fileObject);
    try {
      const [presignedUrlResult] = await ControlPlaneClientUtils.getPresignedUrls(
        this.controlPlaneClient,
        PresignedUrlAction.PUT,
        [key],
      );

      return presignedUrlResult;
    } catch (error) {
      logger.error('Error occurred while fetching presigned URL', { error });
      throw error;
    }
  }

  /**
   * Fetches a file from a URL and converts it to a File object
   * @param {string} url - URL to fetch file from
   * @returns {Promise<File>}
   * @throws {Error} When fetch fails or content type is missing
   * @private
   */
  private async fetchUrlFileObject(url: string): Promise<File> {
    try {
      const urlObject = new URL(url);
      const response = await fetch(urlObject);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType) {
        throw new Error('Content type not provided by server');
      }
      const blob = await response.blob();
      const filenameWithExtension = this.getFileNameWithExtension(urlObject.pathname, contentType);
      return new File([blob], filenameWithExtension, {
        type: contentType,
        lastModified: Date.now(),
      });
    } catch (error) {
      const err = new Error(`Failed to fetch file from ${url}: ${getErrorMessage(error)}`);
      err.cause = error;
      throw err;
    }
  }

  /**
   * Fetches metadata from a URL using HEAD request
   * @param {string} url - URL to fetch metadata from
   * @returns {Promise<Headers>}
   * @throws {Error} When HEAD request fails
   * @private
   */
  private async fetchUrlMetadata(url: string): Promise<Headers> {
    const urlObject = new URL(url);
    const headResponse: Response = await fetch(urlObject, {
      method: 'HEAD',
      signal: AbortSignal.timeout(TIMEOUT_IN_MS),
    });
    if (!headResponse.ok) {
      throw new Error(`HTTP error! status: ${headResponse.status}`);
    }
    logger.debug('Headers:', { headers: headResponse.headers });
    return headResponse.headers;
  }

  /**
   * Generates a unique S3 key for file storage
   * @param {string} keyPrefix - Prefix for the S3 key
   * @param {File} fileObject - File object to generate key for
   * @returns {string} Generated S3 key
   * @private
   */
  private generateS3Key(keyPrefix: string, fileObject: File): string {
    const uniqueId = uuid();
    const originalFilename = fileObject.name;
    let sanitizedName = this.sanitizeFilename(originalFilename);
    const extension = originalFilename.split('.').pop() || '';
    const timestamp = Date.now();
    sanitizedName = sanitizedName.substring(0, AssetConstants.MAX_FILENAME_LENGTH);
    return `${keyPrefix}/${timestamp}_${uniqueId}_${sanitizedName}${extension ? '.' + extension : ''}`;
  }

  /**
   * Generates a filename with appropriate extension based on content type
   * @param {string} pathname - Original pathname
   * @param {string} contentType - MIME type of the content
   * @returns {string} Generated filename with extension
   * @private
   */
  private getFileNameWithExtension(pathname: string, contentType: string): string {
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1).split('.')[0];
    const extension = mime.extension(contentType) || 'bin';
    return `${fileName}.${extension}`;
  }

  /**
   * Processes a file object through validation and upload
   * @param {File} fileObject - File to process
   * @returns {Promise<void>}
   * @private
   */
  private async handleFileObject(fileObject: File): Promise<void> {
    const dimensionSpecs: DimensionSpecs = {
      minWidth: this.validationProps.minResolution.width,
      minHeight: this.validationProps.minResolution.height,
      maxWidth: this.validationProps.maxResolution.width,
      maxHeight: this.validationProps.maxResolution.height,
      aspectRatio: this.validationProps.expectedAspectRatio,
    };
    await this.validateFileObject(fileObject, dimensionSpecs);
    const { presignedUrl, key } = await this.fetchPresignedUrl(fileObject);
    await this.uploadUsingPresignedUrl(presignedUrl, fileObject);
    this.setState({ status: AssetStatus.COMPLETED, key });
  }

  /**
   * Sanitizes a filename for safe storage
   * @param {string} filename - Original filename
   * @returns {string} Sanitized filename
   * @private
   */
  private sanitizeFilename(filename: string): string {
    try {
      // Handle filenames without extensions properly
      const lastDotIndex = filename.lastIndexOf('.');
      const nameWithoutExtension =
        lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;

      const encoded = encodeURIComponent(nameWithoutExtension);
      const sanitized = encoded
        .replace(/%20/g, '_')
        .replace(/%[0-9A-Fa-f]{2}/g, '_')
        .replace(/[^a-zA-Z0-9_.-]/g, '_');

      // Ensure non-empty result
      return sanitized || FALLBACK_FILE_NAME;
    } catch (error) {
      logger.error('Error sanitizing filename', { error });
      return FALLBACK_FILE_NAME;
    }
  }

  /**
   * Sets the asset state to in-progress
   * @private
   */
  private setInProgress(): void {
    this.setState({ status: AssetStatus.PENDING_UPLOAD, error: undefined });
  }

  /**
   * Uploads a file using a presigned URL
   * @param {string} presignedUrl - Presigned URL for upload
   * @param {File} fileObject - File to upload
   * @returns {Promise<void>}
   * @throws {Error} When upload fails
   * @private
   */
  private async uploadUsingPresignedUrl(presignedUrl: string, fileObject: File): Promise<void> {
    logger.debug('Uploading file using presigned URL', { presignedUrl });
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: fileObject,
        headers: {
          'Content-Type': fileObject.type,
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
      logger.debug('File uploaded successfully', { response });
    } catch (error) {
      const err = new Error(
        `Failed to upload file '${fileObject.name}': ${getErrorMessage(error)}`,
      );
      err.cause = error;
      throw err;
    }
  }

  private fetchErrorMessage(error: unknown, fileName: string): string {
    return error instanceof Error &&
      error.message &&
      (error.message.includes('Failed to upload file') ||
        error.message.includes('Failed to get presigned URL'))
      ? getErrorMessage(error)
      : fileName
        ? `Failed to upload file '${fileName}': ${getErrorMessage(error)}`
        : getErrorMessage(error);
  }
}

export { type AbstractAssetInputHandlerProps, AbstractAssetInputHandler };
